import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { 
    fetchSystemStats, 
    fetchSystemHealth, 
    fetchUserAnalytics,
    fetchSecurityDashboard 
} from '../../store/slices/adminReducer';
import AdminSidebar from '../../components/Admin/AdminSidebar';
import AdminHeader from '../../components/Admin/AdminHeader';
import StatsCard from '../../components/Admin/StatsCard';

function AdminDashboard() {
    const user = useSelector((state) => state.affiliate.auth);
    const { system, analytics } = useSelector((state) => state.admin);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    // Check if the user is an admin
    useEffect(() => {
        if (user?.affiliate_role !== "admin") {
            navigate("/login");
        }
    }, [user, navigate]);

    // Fetch dashboard data
    useEffect(() => {
        if (user?.affiliate_role === "admin") {
            dispatch(fetchSystemStats());
            dispatch(fetchSystemHealth());
            dispatch(fetchUserAnalytics());
            dispatch(fetchSecurityDashboard());
        }
    }, [dispatch, user]);

    // handleLogout unused currently; keep for future but comment to avoid lint
    // const handleLogout = async () => {
    //     try {
    //         await dispatch(logout());
    //         navigate("/login");
    //     } catch (error) {
    //         console.error("Logout failed:", error);
    //         navigate("/login");
    //     }
    // };

    // Render nothing if user is not loaded yet or is not an admin
    if (!user || user?.affiliate_role !== "admin") {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-secondary-green border-t-transparent mx-auto"></div>
                    <p className="mt-6 text-gray-600 text-lg font-medium">Checking admin permissions...</p>
                </div>
            </div>
        );
    }

    const stats = system?.stats || {};
    const analyticsData = analytics?.users || {};

    return (
        <div className="min-h-screen bg-gray-100 flex">
            {/* Sidebar */}
            <AdminSidebar 
                collapsed={sidebarCollapsed} 
                onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
            />

            {/* Main Content */}
            <div className="flex-1 flex flex-col">
                {/* Header */}
                <AdminHeader 
                    onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
                    sidebarCollapsed={sidebarCollapsed}
                />

                {/* Dashboard Content */}
                <div className="flex-1 p-6">
                    {/* Page Header */}
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
                        <p className="text-gray-600">
                            Welcome back, {user.name}. Here's what's happening with your system.
                        </p>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-4 mobile:grid-cols-1 tablet:grid-cols-2 laptop:grid-cols-2 desktop:grid-cols-4 gap-6 mb-8">
                        <StatsCard
                            title="Total Users"
                            value={stats?.users?.total || 0}
                            change={analyticsData?.growth?.length > 0 ? analyticsData.growth[analyticsData.growth.length - 1]?.new_users : 0}
                            changeType="positive"
                            icon="👥"
                            color="blue"
                            loading={system.loading}
                        />
                        <StatsCard
                            title="Active Users"
                            value={stats?.users?.active || 0}
                            change={5.2}
                            changeType="positive"
                            icon="✅"
                            color="green"
                            loading={system.loading}
                        />
                        <StatsCard
                            title="Total Recipes"
                            value={stats?.dishes?.total || 0}
                            change={12.5}
                            changeType="positive"
                            icon="🍽️"
                            color="purple"
                            loading={system.loading}
                        />
                        <StatsCard
                            title="System Health"
                            value={`${system?.health?.overall === 'healthy' ? '98' : '85'}%`}
                            change={-2.1}
                            changeType={system?.health?.overall === 'healthy' ? 'positive' : 'negative'}
                            icon="⚡"
                            color={system?.health?.overall === 'healthy' ? 'green' : 'yellow'}
                            loading={system.loading}
                        />
                    </div>

                    {/* Quick Actions */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">User Management</h3>
                                <span className="text-2xl">👥</span>
                            </div>
                            <p className="text-gray-600 mb-4">Manage users, roles, and permissions</p>
                            <Link 
                                to="/admin/users"
                                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
                            >
                                Manage Users
                                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </Link>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">Analytics</h3>
                                <span className="text-2xl">📊</span>
                            </div>
                            <p className="text-gray-600 mb-4">View system analytics and reports</p>
                            <Link 
                                to="/admin/analytics"
                                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
                            >
                                View Analytics
                                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </Link>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">System Settings</h3>
                                <span className="text-2xl">⚙️</span>
                            </div>
                            <p className="text-gray-600 mb-4">Configure system settings and preferences</p>
                            <Link 
                                to="/admin/settings"
                                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
                            >
                                Configure Settings
                                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </Link>
                        </div>
                    </div>

                    {/* Recent Activity */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
                        <div className="space-y-3">
                            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                    <span className="text-green-600 text-sm">✓</span>
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">System backup completed</p>
                                    <p className="text-xs text-gray-500">2 minutes ago</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <span className="text-blue-600 text-sm">👤</span>
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">New user registered</p>
                                    <p className="text-xs text-gray-500">15 minutes ago</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                                <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                                    <span className="text-yellow-600 text-sm">⚠</span>
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">High memory usage detected</p>
                                    <p className="text-xs text-gray-500">1 hour ago</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AdminDashboard;
